import { ApiClient, ApiError } from '@ochal/api-client';
import type { CurrentUser, RoleCode } from '@ochal/types';
import { coordinateRefresh, watchRefresh } from './refresh-coordinator';
import {
  SessionExpiredError,
  type AuthAdapter,
  type AuthSession,
  type AuthTokens,
  type LoginCredentials,
} from './types';
const STORAGE_KEY = 'ochal.auth.session';
interface ApiEnvelope<T> {
  data: T;
}
interface ApiUser {
  id: string;
  username: string;
  phone: string | null;
  fullName: string;
  roles: string[];
  permissions: string[];
  avatarUrl?: string;
}
interface ApiAuthResult {
  user: ApiUser;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  sessionExpiresAt: number;
  idleTimeoutMinutes: number;
}
const roleCodes: readonly RoleCode[] = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER'];
function mapUser(user: ApiUser): CurrentUser {
  const role =
    user.roles.find((item): item is RoleCode => roleCodes.includes(item as RoleCode)) ?? 'VIEWER';
  return {
    id: user.id,
    username: user.username,
    fullName: user.fullName,
    mobile: user.phone ?? undefined,
    role,
    permissions: user.permissions,
    avatarUrl: user.avatarUrl,
  };
}
export class ApiAuthAdapter implements AuthAdapter {
  private readonly publicClient: ApiClient;
  private readonly authorizedClient: ApiClient;
  private refreshInFlight: Promise<string | null> | null = null;
  constructor(baseUrl: string, timeout = 15_000) {
    this.publicClient = new ApiClient({ baseUrl, timeout });
    this.authorizedClient = new ApiClient({
      baseUrl,
      timeout,
      getAccessToken: () => this.read()?.tokens.accessToken ?? null,
      refreshAccessToken: () => this.refreshAccessToken(),
    });
    watchRefresh(() => this.read(), (session) => this.write(session, Boolean(localStorage.getItem(STORAGE_KEY))));
  }
  getAccessToken = (): string | null => this.read()?.tokens.accessToken ?? null;
  refreshAccessToken(): Promise<string | null> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.performRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }
  private async performRefresh(): Promise<string | null> {
    const session = this.read();
    if (!session) return null;
    try {
      const next = await coordinateRefresh(
        session,
        () => this.read(),
        (value) => this.write(value, Boolean(localStorage.getItem(STORAGE_KEY))),
        async (value) => {
          await this.refresh(value.tokens.refreshToken);
          const updated = this.read();
          if (!updated) throw new SessionExpiredError();
          return updated;
        },
      );
      return next?.tokens.accessToken ?? null;
    } catch (error) {
      if (error instanceof SessionExpiredError) {
        await this.clear();
        return null;
      }
      throw error;
    }
  }
  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const response = await this.publicClient.post<ApiEnvelope<ApiAuthResult>>(
      '/auth/login',
      credentials,
    );
    const session = this.toSession(response.data);
    this.write(session, credentials.remember);
    return session;
  }
  async logout(): Promise<void> {
    try {
      await this.authorizedClient.post<void>('/auth/logout', {});
    } catch {
      /* A local logout must remain available when the API is unavailable. */
    } finally {
      await this.clear();
    }
  }
  async refresh(refreshToken: string): Promise<AuthTokens> {
    try {
      const response = await this.publicClient.post<ApiEnvelope<ApiAuthResult>>('/auth/refresh', {
        refreshToken,
      });
      const session = this.toSession(response.data);
      this.write(session, Boolean(localStorage.getItem(STORAGE_KEY)));
      return session.tokens;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) throw new SessionExpiredError();
      throw error;
    }
  }
  async getSession(): Promise<AuthSession | null> {
    const session = this.read();
    if (!session) return null;
    if (session.tokens.expiresAt <= Date.now() + 10_000) {
      try {
        await this.refreshAccessToken();
      } catch (error) {
        if (error instanceof SessionExpiredError) throw error;
        return this.read();
      }
      return this.read();
    }
    try {
      const response = await this.authorizedClient.get<ApiEnvelope<ApiUser>>('/auth/me');
      const current = this.read();
      if (!current) return null;
      const next = { ...current, user: mapUser(response.data) };
      this.write(next, Boolean(localStorage.getItem(STORAGE_KEY)));
      return next;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && (await this.refreshAccessToken()))
        return this.read();
      if (error instanceof ApiError && error.status === 401) {
        await this.clear();
        throw new SessionExpiredError();
      }
      // Offline/timeout/5xx is not evidence that authentication expired.
      return this.read();
    }
  }
  async touchSession(): Promise<void> {
    try {
      await this.authorizedClient.get<ApiEnvelope<ApiUser>>('/auth/me');
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        await this.clear();
        throw new SessionExpiredError();
      }
      throw error;
    }
  }
  private toSession(result: ApiAuthResult): AuthSession {
    const policy =
      Number.isFinite(result.idleTimeoutMinutes) && Number.isFinite(result.sessionExpiresAt)
        ? {
            idleTimeoutMs: result.idleTimeoutMinutes * 60_000,
            absoluteExpiresAt: result.sessionExpiresAt,
          }
        : undefined;
    return {
      user: mapUser(result.user),
      tokens: {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
      },
      ...(policy ? { policy } : {}),
    };
  }
  private read(): AuthSession | null {
    const raw = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthSession;
    } catch {
      void this.clear();
      return null;
    }
  }
  private write(session: AuthSession, remember: boolean): void {
    const target = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    other.removeItem(STORAGE_KEY);
    target.setItem(STORAGE_KEY, JSON.stringify(session));
  }
  private async clear(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  }
}
